import { useEffect, useState } from 'react';
import { mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { NecesitaConexion } from '@/ui/NecesitaConexion';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { formatCents, MESES } from '@/features/catalog/catalog';
import { exportarPdfCierre } from '@/features/reports/exportFiles';
import { fetchClosureExport } from '@/features/reports/reports';
import { AdjustmentForm } from './AdjustmentForm';
import { SelectorPeriodo } from '@/features/period/SelectorPeriodo';
import { ConfirmarBorrado } from '@/ui/ConfirmarBorrado';
import {
  FILTROS_VACIOS,
  listAllParticipants,
  listWorks,
  type Participant,
  type WorkListRow,
} from '@/features/works/works';
import { AvisoFlotante } from '@/ui/AvisoFlotante';
import { Boton, EncabezadoPantalla, EncabezadoSeccion, Importe, Seccion } from '@/ui/Sistema';
import {
  closePeriod,
  fetchClosureDetail,
  listAdjustments,
  listClosures,
  reopenPeriod,
  type AdjustmentBatch,
  type Closure,
  listClosingBlockers,
  MOTIVOS_DE_BLOQUEO,
  type ClosingBlocker,
  type ClosureDetail,
  deleteAdjustment,
  fetchOrganizationDebt,
  type DeudaDeLaOrganizacion,
} from './closing';
interface Props {
  onVolver: () => void;
  /**
   * Abre el listado de trabajos filtrado por ese cliente y período. Sin esto,
   * saber qué trabajo bloquea el cierre no sirve de nada: hay que ir a
   * buscarlo a mano.
   */
  /** Abre el listado de trabajos del mes que se está mirando. */
  onVerTrabajos?: () => void;
}
/**
 * Cierre mensual (Spec §29–§31).
 *
 * La pantalla no calcula nada: muestra lo que la base congeló. Cerrar y reabrir
 * son llamadas a `close_period` / `reopen_period`, que validan los invariantes
 * antes de escribir y fallan enteras si algo no da.
 *
 * Lo que ve cada uno lo decide RLS, no este componente: un miembro recibe su
 * propia línea de liquidación y las transferencias donde interviene; el
 * administrador, la tabla completa.
 */
export function ClosingPage({ onVolver, onVerTrabajos }: Props) {
  const { activeOrg } = useOrg();
  // Mismo período que el dashboard, sin volver a decidirlo.
  const { year, month, setPeriod } = usePeriod();
  const orgId = activeOrg?.organization_id;
  const esAdmin = activeOrg?.is_admin ?? false;
  const [closures, setClosures] = useState<Closure[]>([]);
  const [detalle, setDetalle] = useState<ClosureDetail | null>(null);
  const [ajustes, setAjustes] = useState<AdjustmentBatch[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [motivo, setMotivo] = useState('');
  const [reabriendo, setReabriendo] = useState<string | null>(null);
  const [bloqueos, setBloqueos] = useState<ClosingBlocker[]>([]);
  const [ajustando, setAjustando] = useState(false);
  const [borrandoAjuste, setBorrandoAjuste] = useState<string | null>(null);
  const [deuda, setDeuda] = useState<DeudaDeLaOrganizacion[]>([]);
  const [gente, setGente] = useState<Participant[]>([]);
  const [avance, setAvance] = useState<WorkListRow[]>([]);
  // Después de liquidar, el mes NO cambia solo: el selector manda. Se ofrece
  // pasar al siguiente, que es distinto de hacerlo por su cuenta.
  const [sugerirSiguiente, setSugerirSiguiente] = useState(false);
  const [pdfDe, setPdfDe] = useState<string | null>(null);
  // El PDF se arma con lo que quedó CONGELADO en esa versión, no con lo que
  // valen hoy los trabajos: por eso se pide por id de versión.
  const descargarPdf = async (versionId: string) => {
    setError(null);
    setPdfDe(versionId);
    try {
      const datos = await fetchClosureExport(versionId);
      await exportarPdfCierre(datos);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setPdfDe(null);
    }
  };
  const periodo = closures.find((c) => c.period_year === year && c.period_month === month);
  const cerrado = periodo?.status === 'closed';
  /** Lo que la organización le debe a este participante en el período. */
  const deudaDe = (pid: string) => deuda.find((d) => d.participant_id === pid)?.amount_cents ?? 0;
  /**
   * La versión vigente del período.
   *
   * Se DERIVA, no se guarda en estado: el usuario no elige una versión. Un mes
   * es UN mes, y cerrar, reabrir y volver a cerrar es corregirse, no crear tres
   * cosas. El modelo siempre lo tuvo así —`monthly_closures` tiene una fila por
   * período, con su `status`— y las versiones son el rastro interno de esas
   * correcciones. La pantalla las mostraba como si fueran decisiones de quien
   * usa la aplicación, y lo obligaba a navegar una lista creciente para llegar
   * al único cierre que le importa: el vigente.
   *
   * Guardarlo en estado obligaba además a sincronizarlo con un efecto, que en
   * React 19 encadena renders. Derivado no hay nada que sincronizar: cuando
   * `closures` se recarga, esto ya es el valor nuevo.
   */
  const versionAbierta = periodo?.current_version_id ?? null;
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const [cs, as, dd, pp, ww] = await Promise.all([
          listClosures(orgId),
          listAdjustments(orgId, year, month),
          fetchOrganizationDebt(orgId, year, month),
          // La que INCLUYE la cuenta técnica: se la usa sólo para reconocerla
          // en la liquidación, no para ofrecerla en ningún desplegable.
          listAllParticipants(orgId),
          // Para el avance del mes: cuántos van facturados y cuántos cobrados.
          listWorks(orgId, { ...FILTROS_VACIOS, year, month }),
        ]);
        if (cancelado) return;
        setClosures(cs);
        setAjustes(as);
        setDeuda(dd);
        setGente(pp);
        setAvance(ww);
        setError(null);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month, recarga]);
  useEffect(() => {
    if (!versionAbierta) return;
    let cancelado = false;
    void (async () => {
      try {
        const d = await fetchClosureDetail(versionAbierta);
        if (!cancelado) setDetalle(d);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [versionAbierta, recarga]);
  /**
   * Volver a calcular la liquidación de un período ya liquidado.
   *
   * Reabre y vuelve a cerrar. No es un rodeo: reabrir es lo que deja escribir
   * una versión nueva, y el rastro de por qué se recalculó queda igual que
   * cualquier otra reapertura. Para quien mira, es un solo botón.
   */
  const recalcular = async () => {
    if (!orgId || !periodo) return;
    setError(null);
    setOcupado(true);
    try {
      await reopenPeriod(periodo.closure_id, 'Recalcular: hubo cambios posteriores al cálculo');
      await closePeriod(orgId, year, month);
      setAviso(`${MESES[month - 1]} ${year} se recalculó.`);
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  };
  const borrarAjuste = async (batchId: string) => {
    setError(null);
    try {
      await deleteAdjustment(batchId);
      setBorrandoAjuste(null);
      setAviso('Ajuste borrado.');
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };
  const recargar = () => setRecarga((n) => n + 1);
  const cerrar = async () => {
    if (!orgId) return;
    setError(null);
    setAviso(null);
    setBloqueos([]);
    setOcupado(true);
    try {
      await closePeriod(orgId, year, month);
      setAviso(`${MESES[month - 1]} ${year} quedó cerrado.`);
      setSugerirSiguiente(true);
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
      // El mensaje de la base explica el motivo pero enumera UUIDs. Se piden
      // los mismos trabajos con nombre y fecha, para poder hacer algo con ellos.
      try {
        setBloqueos(await listClosingBlockers(orgId, year, month));
      } catch {
        // Si tampoco se pueden listar, queda el mensaje de la base: menos útil,
        // pero no se pierde la información de que el cierre fue rechazado.
      }
    } finally {
      setOcupado(false);
    }
  };
  const reabrir = async (closureId: string) => {
    setError(null);
    setAviso(null);
    setOcupado(true);
    try {
      await reopenPeriod(closureId, motivo);
      setAviso('Período reabierto. Ya podés corregir los trabajos.');
      setReabriendo(null);
      setMotivo('');
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  };
  // Sin servidor no hay nada honesto que mostrar acá: haría falta el estado
  // de ahora, y de ahora sólo sabe el servidor.
  if (useSinConexion()) {
    return (
      <div className="flex flex-col gap-5 p-4">
        <NecesitaConexion
          que="Liquidar un mes calcula contra los trabajos tal como están en el servidor."
          testId="liquidacion-sin-conexion"
        />
      </div>
    );
  }

  if (!activeOrg || !orgId) return null;
  return (
    <div className="flex flex-col gap-4 p-4" data-testid="closing-page">
      <EncabezadoPantalla
        titulo="Liquidación"
        nota="Cuánto ganó cada uno, y quién le transfiere a quién"
        onVolver={onVolver}
      />
      {/* Antes esta pantalla mostraba el mes y no dejaba cambiarlo: para ver
          otro había que volver al resumen, moverlo allá y entrar de nuevo. */}
      <SelectorPeriodo testId="liquidacion-periodo" />
      {/* El mes se va cerrando trabajo por trabajo, no de un golpe. Esto es lo
          que deja ver cuánto falta: liquidar da los números, pero el período
          queda consolidado de verdad cuando no quedan trabajos por facturar. */}
      {avance.length > 0 && (
        <section className="flex flex-col gap-3 superficie p-4" data-testid="avance">
          <p className="text-sm font-medium">
            {avance.length} trabajo{avance.length === 1 ? '' : 's'} en {MESES[month - 1]}
          </p>
          {/* Cuatro números y un botón. Antes cada número era un enlace a la
              lista ya filtrada por ese estado; Trabajos dejó de tener filtros
              —el mes es su único eje— así que abrir una lista recortada sin
              poder ver ni sacar el recorte sería el peor de los dos mundos.
              Acá se informa; la lista es la lista. */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[
              {
                clave: 'por-facturar',
                texto: 'por facturar',
                cuenta: avance.filter((w) => !w.is_billed).length,
              },
              {
                clave: 'facturados',
                texto: 'facturados',
                cuenta: avance.filter((w) => w.is_billed).length,
              },
              {
                clave: 'por-cobrar',
                texto: 'por cobrar',
                cuenta: avance.filter((w) => w.is_billed && !w.is_paid).length,
              },
              {
                clave: 'cobrados',
                texto: 'cobrados',
                cuenta: avance.filter((w) => w.is_paid).length,
              },
            ].map((c) => (
              <div
                key={c.clave}
                className="rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 py-2"
                data-testid={`avance-${c.clave}`}
              >
                <span className="block text-lg font-semibold tabular-nums">{c.cuenta}</span>
                <span className="block text-xs text-[var(--tinta-suave)]">{c.texto}</span>
              </div>
            ))}
          </div>
          {onVerTrabajos && (
            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="min-h-11 self-start rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-4 text-sm"
              data-testid="ver-trabajos"
            >
              Ver trabajos de {MESES[month - 1]}
            </button>
          )}
          {avance.every((w) => w.is_billed) ? (
            <p className="text-sm text-[var(--exito)]">
              Todos facturados: el mes quedó consolidado.
            </p>
          ) : (
            <p className="text-sm text-[var(--tinta-suave)]">
              Los que no están facturados se siguen pudiendo corregir, y liquidar el mes no los
              congela.
            </p>
          )}
        </section>
      )}
      {/* Un mes, un estado. Cerrar, reabrir y volver a cerrar es corregirse:
          sigue siendo el mismo Agosto 2026, no tres cosas distintas. */}
      <p className="flex items-baseline gap-2 text-sm" data-testid="estado-periodo">
        <span className="text-[var(--tinta-suave)]">Estado del período</span>
        <span
          className={`rounded-full px-3 py-1 font-medium ${
            cerrado
              ? 'bg-[var(--accion)] text-[var(--sobre-accion)]'
              : 'bg-[var(--exito-fondo)] text-[var(--exito)]'
          }`}
          data-testid="period-status"
        >
          {cerrado ? 'Cerrado' : 'Abierto'}
        </span>
      </p>
      {/* Si está al día, un renglón y nada más. El cartel grande es para
          cuando algo pasa; cuando no pasa nada, decirlo en voz baja. */}
      {periodo?.current_version_id && !periodo.stale_since && (
        <p className="flex items-center gap-2 text-sm" data-testid="liquidacion-vigente">
          <span className="size-2 rounded-full bg-[var(--exito)]" aria-hidden />
          <span className="text-[var(--exito)]">Liquidación vigente</span>
        </p>
      )}

      {/* Una liquidación que dejó de estar al día no se presenta como vigente.
          No se borra la anterior —sirve para saber qué se calculó y cuándo—
          pero se dice, arriba de todo, que hay cambios posteriores. */}
      {periodo?.stale_since && (
        <section
          className="flex flex-col gap-2 superficie border-[var(--aviso)] bg-[var(--aviso-fondo)] p-4 text-[var(--aviso)] "
          data-testid="liquidacion-vieja"
        >
          <h2 className="font-medium">Liquidación desactualizada</h2>
          <p className="text-sm">
            Hay cambios en los trabajos posteriores al último cálculo. Los números de abajo son los
            de entonces, no los de ahora.
          </p>
          {esAdmin && (
            <button
              type="button"
              onClick={() => void recalcular()}
              disabled={ocupado}
              className="min-h-11 self-start rounded-[var(--radius-control)] border border-[var(--aviso)] px-4 text-sm disabled:opacity-50"
              data-testid="recalcular"
            >
              {ocupado ? 'Recalculando…' : 'Recalcular liquidación'}
            </button>
          )}
        </section>
      )}
      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="closing-error">
          {mensajeDeError(error)}
        </p>
      )}
      {bloqueos.length > 0 && (
        <section
          className="flex flex-col gap-2 superficie border-[var(--aviso)] bg-[var(--aviso-fondo)] p-4 text-[var(--aviso)] "
          data-testid="closing-blockers"
        >
          <h2 className="font-medium">
            {bloqueos.length === 1
              ? 'Hay un trabajo que impide cerrar'
              : `Hay ${bloqueos.length} trabajos que impiden cerrar`}
          </h2>
          <ul className="flex flex-col divide-y divide-[var(--aviso)]">
            {bloqueos.map((b) => (
              <li key={b.work_session_id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {new Date(`${b.work_date}T00:00:00`).toLocaleDateString('es-AR')} ·{' '}
                    {b.client_name}
                    {b.establishment_name ? ` · ${b.establishment_name}` : ''}
                  </p>
                  {b.services && <p className="truncate text-xs opacity-80">{b.services}</p>}
                  <p className="text-xs font-medium">{MOTIVOS_DE_BLOQUEO[b.reason]}</p>
                </div>
                {onVerTrabajos && (
                  <button
                    type="button"
                    onClick={() => onVerTrabajos()}
                    className="min-h-11 shrink-0 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] border-[var(--aviso)] px-3 text-xs"
                    data-testid="blocker-abrir"
                  >
                    Ver
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {aviso && (
        <p className="text-sm text-[var(--exito)]" data-testid="closing-notice">
          {aviso}
        </p>
      )}
      {/* --- Acciones del administrador ---
          El estado NO se repite acá: ya lo dice el chip de arriba, y verlo dos
          veces seguidas con distinta redacción hace dudar de si son dos cosas
          distintas. */}
      <section className="flex flex-col gap-3">
        {esAdmin && !cerrado && (
          <>
            <button
              type="button"
              onClick={() => void cerrar()}
              disabled={ocupado}
              className="min-h-12 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 font-medium text-[var(--sobre-accion)] disabled:opacity-50"
              data-testid="close-period"
            >
              {ocupado ? 'Liquidando…' : 'Liquidar el período'}
            </button>
            <p className="text-xs text-[var(--tinta-tenue)]">
              Liquidar calcula quién le debe a quién y deja el resultado guardado. No congela los
              trabajos: los que todavía no están facturados se siguen pudiendo corregir, y lo que
              congela un trabajo es facturarlo. Si algún trabajo del mes no tiene precio de
              referencia, la liquidación se rechaza entera y no se guarda nada.
            </p>
          </>
        )}
        {esAdmin && cerrado && periodo && (
          <>
            {reabriendo === periodo.closure_id ? (
              <div className="flex flex-col gap-2">
                {/* El motivo dejó de ser obligatorio.
                    Lo era, y lo único que conseguía era que se escribiera
                    «error» para poder seguir. Quien reabre es la misma persona
                    que acaba de decidirlo, en su propia veterinaria y casi
                    siempre para corregir algo suyo: pedirle que lo justifique
                    por escrito es cobrarle peaje por arreglarse.

                    Lo que hace falta para reconstruir qué pasó con un mes —quién
                    lo reabrió y cuándo— se registra solo. */}
                <label className="flex flex-col gap-1 text-sm">
                  Motivo (opcional)
                  <input
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Si querés dejar anotado por qué"
                    className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-base"
                    data-testid="reopen-reason"
                  />
                  <span className="text-xs text-[var(--tinta-tenue)]">
                    Tu nombre y la fecha quedan registrados igual.
                  </span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void reabrir(periodo.closure_id)}
                    disabled={ocupado}
                    className="min-h-11 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm disabled:opacity-50"
                    data-testid="reopen-confirm"
                  >
                    Reabrir
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReabriendo(null);
                      setMotivo('');
                    }}
                    className="min-h-11 px-3 text-sm underline opacity-70"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setReabriendo(periodo.closure_id)}
                className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-4 text-sm"
                data-testid="reopen-period"
              >
                Reabrir el período
              </button>
            )}
          </>
        )}
        {!esAdmin && (
          <p className="text-xs text-[var(--tinta-tenue)]">
            Cerrar y reabrir un período es tarea del administrador.
          </p>
        )}
      </section>
      {/* --- Ajustes del período (D3) --- */}
      <section className="flex flex-col gap-3 superficie p-4" data-testid="adjustments">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-medium">Ajustes</h2>
          {esAdmin && !cerrado && (
            <button
              type="button"
              onClick={() => setAjustando((v) => !v)}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
              data-testid="new-adjustment"
            >
              {ajustando ? 'Cancelar' : '+ Ajuste'}
            </button>
          )}
        </div>
        {ajustando && (
          <AdjustmentForm
            organizationId={orgId}
            year={year}
            month={month}
            onSaved={() => {
              setAjustando(false);
              setAviso('Ajuste registrado.');
              recargar();
            }}
            onError={setError}
          />
        )}
        {ajustes.length === 0 ? (
          <p className="text-sm text-[var(--tinta-suave)]">Sin ajustes en este período.</p>
        ) : (
          <ul
            className="flex flex-col divide-y divide-[var(--linea)]"
            data-testid="adjustment-list"
          >
            {ajustes.map((a) => (
              <li key={a.batch_id} className="flex flex-col gap-1 py-2 text-sm">
                <span className="flex items-start justify-between gap-2">
                  <span className="font-medium">{a.concept}</span>
                  {/* Un ajuste con un precio mal tipeado no tenía salida: no se
                      podía editar ni borrar, y corregirlo exigía armar a mano el
                      asiento inverso invirtiendo las dos patas. */}
                  {esAdmin && !cerrado && (
                    <button
                      type="button"
                      onClick={() => setBorrandoAjuste(a.batch_id)}
                      className="min-h-11 shrink-0 text-xs underline opacity-70"
                      data-testid="borrar-ajuste"
                    >
                      Borrar
                    </button>
                  )}
                </span>
                <span className="text-xs text-[var(--tinta-suave)]">{a.reason}</span>
                {a.entries.map((e) => (
                  <span key={e.participant_id} className="flex justify-between">
                    <span className="opacity-70">{e.display_name}</span>
                    <span
                      className={
                        e.amount_cents < 0 ? 'text-[var(--peligro)]' : 'text-[var(--exito)]'
                      }
                    >
                      {formatCents(e.amount_cents)}
                    </span>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>
      {/* El «Historial de cierres» que estaba acá se sacó de la vista normal.
          Listaba Versión 1, Versión 2, Versión 3… y crecía cada vez que alguien
          se corregía. Para quien usa la aplicación eso no es historia: es la
          misma liquidación de agosto, hecha bien después de haberla hecho mal.
          Obligaba además a elegir de una lista para llegar al único cierre que
          importa, que es el vigente.
          Las versiones NO se borraron ni se dejaron de escribir: siguen en
          `closure_versions`, con quién cerró, cuándo, y con qué motivo se
          reabrió. Es auditoría, y como tal vive en la base y no en la pantalla
          de todos los días. */}
      {/* --- El cierre vigente, sin pasar por ninguna lista --- */}
      {detalle && versionAbierta === detalle.version_id && (
        <section className="flex flex-col gap-6" data-testid="closure-detail">
          {/* Los números eran correctos y aun así había que saber la fórmula
              para leerlos: el saldo aparecía primero, en verde o en rojo, sin
              decir qué significaba el signo. Ahora cada participante se cuenta
              en el orden en que se piensa —cuánto ganó, cuánto ya tiene a su
              nombre, y qué falta para emparejar— y el saldo se dice con
              palabras. La matemática es exactamente la misma.
              La pantalla contesta dos preguntas y en este orden: cuánto ganó
              cada uno, y quién le transfiere a quién. Lo demás —cuántos
              trabajos, el total, el PDF— es contexto y va al final. */}
          <EncabezadoSeccion
            titulo={detalle.is_admin ? 'Ganancia del período' : 'Mi liquidación'}
            nota={`${MESES[detalle.period_month - 1]} ${detalle.period_year}`}
          />
          <ul
            className="flex flex-col divide-y divide-[var(--linea)]"
            data-testid="settlement-table"
          >
            {detalle.entries.map((e) => (
              <li key={e.participant_id} className="flex flex-col gap-1 py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 font-semibold">
                    {e.display_name}
                    {/* Sin esta aclaración se lee como un socio más de la
                        veterinaria, cuando es una cuenta técnica: lo que quedó
                        sin dueño asignado se le imputa a ella para que la
                        liquidación siga cerrando en cero. */}
                    {/* Por el TIPO, no por el nombre. Antes esto miraba si el
                        nombre empezaba con «Organización», y esa línea era lo
                        único en toda la aplicación que impedía renombrar la
                        cuenta técnica: la base la identifica por `kind` en nueve
                        lugares y por nombre en ninguno. Una veterinaria que
                        quiera llamarla «Caja» o «Estudio» tiene que poder. */}
                    {gente.find((g) => g.id === e.participant_id)?.kind === 'org_account' && (
                      <span
                        className="ml-2 rounded-full bg-[var(--superficie-suave)] px-2 py-0.5 text-xs font-normal opacity-80"
                        title="No es una persona. Acá va lo que no tiene facturador asignado, para que la liquidación cierre en cero."
                        data-testid="contrapartida-nota"
                      >
                        cuenta de la organización
                      </span>
                    )}
                  </span>
                  <Importe className="shrink-0 text-lg" data-testid="settlement-ganancia">
                    {formatCents(e.earned_honoraria_cents + e.earned_mobility_cents)}
                  </Importe>
                </div>
                <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                  <span>De eso, movilidad</span>
                  <span>{formatCents(e.earned_mobility_cents)}</span>
                </div>
                <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                  <span title="Total facturado a nombre de esta persona en el período, cobrado o no.">
                    Facturación asignada
                  </span>
                  <span>{formatCents(e.billed_cents)}</span>
                </div>
                {e.adjustments_cents !== 0 && (
                  <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                    <span>Ajustes</span>
                    <span>{formatCents(e.adjustments_cents)}</span>
                  </div>
                )}
                {/* Hay clientes que no pagan contra factura: llevan el efectivo
                    a la veterinaria. Esa plata entra a la organización y no es
                    ganancia suya; queda debiéndosela a quien hizo el trabajo.
                    Sin este renglón, el saldo de la organización quedaba colgado
                    y no se entendía cuánto de eso le tocaba a cada uno. */}
                {deudaDe(e.participant_id) > 0 && (
                  <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                    <span>La organización le debe</span>
                    <span data-testid="deuda-organizacion">
                      {formatCents(deudaDe(e.participant_id))}
                    </span>
                  </div>
                )}
                {/* El saldo dicho en castellano. En número y con un color, «-$
                    120.000» obliga a recordar de qué lado está el menos: si es
                    plata que falta cobrar o plata que hay que poner. */}
                <p
                  className={`mt-1 text-sm font-medium ${
                    e.balance_cents + deudaDe(e.participant_id) === 0
                      ? 'opacity-60'
                      : e.balance_cents + deudaDe(e.participant_id) > 0
                        ? 'text-[var(--exito)]'
                        : 'text-[var(--peligro)]'
                  }`}
                  data-testid="settlement-saldo"
                >
                  {(() => {
                    // El saldo final reconoce lo que la organización ya le
                    // debe: cobrarlo no es una transferencia entre personas,
                    // pero cambia cuánto queda entre ellas.
                    const s = e.balance_cents + deudaDe(e.participant_id);
                    return s === 0
                      ? 'Está a mano'
                      : s > 0
                        ? `Le tienen que transferir ${formatCents(s)}`
                        : `Tiene que transferir ${formatCents(-s)}`;
                  })()}
                </p>
              </li>
            ))}
          </ul>
          <EncabezadoSeccion titulo="Transferencias" />
          {detalle.transfers.length === 0 ? (
            <p className="text-sm text-[var(--tinta-suave)]" data-testid="no-transfers">
              Nadie le debe nada a nadie: lo que cada uno cobra ya es lo que le corresponde.
            </p>
          ) : (
            <ul
              className="flex flex-col divide-y divide-[var(--linea)]"
              data-testid="transfer-table"
            >
              {detalle.transfers.map((t, i) => (
                <li
                  key={`${t.from_participant_id}-${t.to_participant_id}-${i}`}
                  className="flex items-center justify-between gap-3 py-2 text-sm"
                >
                  {/* Una flecha entre dos nombres se lee en los dos sentidos.
                      Escrito, no. */}
                  <span className="min-w-0">
                    <strong className="font-medium">{t.from_name}</strong> le transfiere a{' '}
                    <strong className="font-medium">{t.to_name}</strong>
                  </span>
                  <Importe className="shrink-0">{formatCents(t.amount_cents)}</Importe>
                </li>
              ))}
            </ul>
          )}
          {!detalle.is_admin && (
            <p className="text-sm text-[var(--tinta-suave)]">
              Ves tu propia liquidación y las transferencias en las que participás.
            </p>
          )}

          {/* El contexto y las acciones, al final. Antes el PDF estaba entre
              el encabezado y los números, o sea entre la pregunta y su
              respuesta. */}
          <Seccion titulo="El período">
            <div className="flex justify-between text-sm">
              <span className="text-[var(--tinta-suave)]">Trabajos</span>
              <span data-testid="snapshot-count">{detalle.snapshot.works_count}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[var(--tinta-suave)]">Total del período</span>
              <Importe>{formatCents(detalle.snapshot.total_cents)}</Importe>
            </div>
            <Boton
              onClick={() => void descargarPdf(detalle.version_id)}
              disabled={pdfDe === detalle.version_id}
              className="self-start"
              data-testid="export-pdf"
            >
              {pdfDe === detalle.version_id ? 'Generando…' : 'Descargar PDF'}
            </Boton>
          </Seccion>
        </section>
      )}
      {borrandoAjuste && (
        <ConfirmarBorrado
          que={`el ajuste «${ajustes.find((a) => a.batch_id === borrandoAjuste)?.concept ?? ''}»`}
          detalle={ajustes
            .find((a) => a.batch_id === borrandoAjuste)
            ?.entries.map((e) => `${e.display_name} ${formatCents(e.amount_cents)}`)
            .join(' · ')}
          palabra="BORRAR"
          onCancelar={() => setBorrandoAjuste(null)}
          onConfirmar={() => void borrarAjuste(borrandoAjuste)}
          testId="confirmar-borrar-ajuste"
        />
      )}
      {/* El mes NO cambia solo al liquidar: el selector es la autoridad sobre
          qué período se está viendo y cargando. Se liquida el 28 y se sigue
          facturando agosto los días siguientes, así que saltar a septiembre por
          su cuenta sería sacarle a alguien la pantalla donde está trabajando.
          Se ofrece, y decide quien está mirando. */}
      {sugerirSiguiente && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="zona-inferior margen-lateral-seguro w-full max-w-2xl rounded-t-2xl border-t border-[var(--linea)] bg-[var(--superficie)] p-4 shadow-xl">
            <div
              className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--linea-fuerte)]"
              aria-hidden
            />
            <p className="text-base font-medium">
              {MESES[month - 1]} {year} liquidado
            </p>
            <p className="mt-1 text-sm text-[var(--tinta-suave)]">
              Podés seguir facturando y cobrando los trabajos de este mes cuando quieras.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setSugerirSiguiente(false)}
                className="min-h-12 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] font-medium"
                data-testid="seguir-en-el-mes"
              >
                Seguir en {MESES[month - 1]}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPeriod(
                    month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 },
                  );
                  setSugerirSiguiente(false);
                }}
                className="min-h-12 flex-1 rounded-[var(--radius-control)] bg-[var(--accion)] font-medium text-[var(--sobre-accion)] "
                data-testid="pasar-al-siguiente"
              >
                Pasar a {MESES[month === 12 ? 0 : month]}
              </button>
            </div>
          </div>
        </div>
      )}
      <AvisoFlotante texto={aviso} onCerrar={() => setAviso(null)} />
    </div>
  );
}
